import {Box, Flex, Textarea} from "@chakra-ui/react"
import {useRef, useEffect, useState} from "react"
import useMentionAutocomplete from "@/components/hooks/useMentionAutocomplete"
import MentionDropdown from "@/pages/scenario/components/MentionDropdown"

const MentionTextArea = ({
    mentionSets = [],
    value,
    onChange,
    contentContainerProps,
    ...props
}) => {
    const highlightRef = useRef(null)
    const [textareaStyles, setTextareaStyles] = useState({})

    const {showList, selectedIndex, filteredVariables, handleInputChange, handleKeyDown, handleBlur, selectVariable, inputRef, activeSet} = useMentionAutocomplete({
        variables: mentionSets[0]?.variables ?? [],
        value,
        onChange,
        wakeUpChar: mentionSets[0]?.wakeUpChar ?? '#',
        wrapMention: mentionSets[0]?.wrapMention ?? false,
        onMentionDelete: mentionSets[0]?.onMentionDelete,
        onVariableSelect: mentionSets[0]?.onVariableSelect,
        variableSets: mentionSets.length > 1 ? mentionSets : undefined,
    })

    useEffect(() => {
        if (!inputRef.current) return
        const s = window.getComputedStyle(inputRef.current)
        setTextareaStyles({
            paddingTop: s.paddingTop,
            paddingRight: s.paddingRight,
            paddingBottom: s.paddingBottom,
            paddingLeft: s.paddingLeft,
            fontSize: s.fontSize,
            fontFamily: s.fontFamily,
            fontWeight: s.fontWeight,
            lineHeight: s.lineHeight,
            letterSpacing: s.letterSpacing,
            borderRadius: s.borderRadius,
        })
    }, [])

    const renderHighlightedText = (text) => {
        const escaped = mentionSets.map(s => `\\${s.wakeUpChar}`).join('|')
        const splitRegex = new RegExp(`(\\{(?:${escaped})[\\w_]+\\})`, 'g')
        const parts = text.split(splitRegex)
        return parts.map((part, i) => {
            for (const set of mentionSets) {
                const re = new RegExp(`^\\{\\${set.wakeUpChar}[\\w_]+\\}$`)
                if (re.test(part)) {
                    const varId = part.slice(2, -1)
                    const isValid = set.variables?.some(v => v.display === varId)
                    if (isValid) {
                        const bg = set.bg ?? 'rgba(66,153,225,0.15)'
                        return (
                            <Box as="span" position="relative" height={"32px"}>
                                <Box
                                    as="span"
                                    onClick={() => set.onMentionClick?.(varId)}
                                    cursor={set.onMentionClick ? "pointer" : 'text'}
                                    key={i}
                                    pointerEvents="auto"
                                    opacity={0}
                                >
                                    {part}
                                </Box>
                                <Flex position={'absolute'} bg={bg} w={'100%'} h={'100%'} as={'span'} left={0} top={0}
                                      color={set.color ?? 'blue.300'} fontSize={12} justifyContent={'center'}
                                      alignItems={'center'} borderRadius={3} fontWeight={500}>
                                    {part}
                                </Flex>
                            </Box>
                        )
                    }
                }
            }
            return <span style={{color: 'white'}} key={i}>{part}</span>
        })
    }

    const handleScroll = (e) => {
        if (highlightRef.current) {
            highlightRef.current.scrollTop = e.target.scrollTop
        }
    }

    const handleClick = (e) => {
        const pos = e.target.selectionStart
        const mentionRegex = new RegExp(
            `\\{(?:${mentionSets.map(s => `\\${s.wakeUpChar}`).join('|')})[\\w_]+\\}`, 'g'
        )
        let match
        while ((match = mentionRegex.exec(value)) !== null) {
            if (pos > match.index && pos < match.index + match[0].length) {
                const endOfMention = match.index + match[0].length
                setTimeout(() => {
                    inputRef.current?.setSelectionRange(endOfMention, endOfMention)
                }, 0)
                break
            }
        }
    }

    return (
        <Box position="relative" width="full" {...contentContainerProps}>
            <Textarea
                ref={inputRef}
                value={value}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onScroll={handleScroll}
                onClick={handleClick}
                onBlur={handleBlur}
                autoComplete="off"
                padding={'12px'}
                paddingTop={'10px'}
                color={'transparent'}
                {...props}
                bg={'gray.800'}
                caretColor={'white'}
            />

            {/* Highlight overlay — mirrors textarea text with styled mentions on top */}
            <Box
                ref={highlightRef}
                aria-hidden="true"
                position="absolute"
                top="0px"
                left="0px"
                right="0px"
                bottom="0px"
                sx={{...textareaStyles}}
                padding={'12px'}
                paddingTop={'10px'}
                bg={'transparent'}
                pointerEvents="none"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
                overflow="hidden"
                zIndex="1"
            >
                {renderHighlightedText(value + "\u200b")}
            </Box>

            <MentionDropdown
                showList={showList}
                filteredVariables={filteredVariables}
                selectedIndex={selectedIndex}
                selectVariable={selectVariable}
                prefix={activeSet?.wakeUpChar ?? '#'}
                selectKey="display"
                showProvider={true}
            />
        </Box>
    )
}

export default MentionTextArea
